import React from 'react';

/**
 * SUSPECTED DEFECT: this declaration and its only producer spell the field
 * differently, and one of the two consumers is therefore dead.
 *
 *   - PRODUCER `contexts/AppContext.tsx` (in the `eligibilityTags.push`) writes
 *     `eligibilityGroups` — the correctly-spelled name.
 *   - This TYPE declares `eligiblityGroups` — missing the second `i`.
 *   - CONSUMER `contexts/OrganizationContext.tsx` reads `?.eligiblityGroups`,
 *     matching the type, so it reads `undefined` at runtime and its
 *     `selectedPayorEligibilityGroup` branch never fires — the eligibility-group
 *     dropdown is silently never populated from that path.
 *   - CONSUMER `components/home/members/index.tsx` reads `?.eligibilityGroups`,
 *     matching the DATA, so it works — and fails typecheck (TS2551) for doing so.
 *
 * The declaration is left exactly as it was. Renaming the field to match the producer
 * is the right repair, but it makes `OrganizationContext`'s branch start firing —
 * a behaviour change, not an annotation. Correcting it here also cascades typecheck
 * errors into `AppContext` and `OrganizationContext`, which is the same signal.
 * Reported, not fixed.
 */
export type EligibilityGroup = {
  eligiblityGroups: string[];
  orgId: string;
};

type Option = {
  key: string;
  text: string;
  value: string;
};

/**
 * The two providers (`MainLayout`, `PatientTab`) fill this from two different stores,
 * with two different guarantees:
 *
 *  - `token` and `userEmail` come from `authState`. Both providers render only under
 *    `<Protected>`, which returns a `<Navigate>` instead of its children unless
 *    `authState.loggedIn`, and `loggedIn` is set true only inside branches already
 *    guarded by `if (token && tokenExpiration && userEmail)`. These two really are
 *    present wherever this context exists.
 *
 *  - SUSPECTED DEFECT (`practitionerId`, `practitionerName`, `gender` are declared
 *    `string` but are `null` early) — REPORTED, NOT FIXED. These come from `appState`,
 *    which `AppContext` seeds `practitionerId: null, practitionerName: null` and fills
 *    from a SEPARATE async load that `<Protected>` does not wait on. So they are
 *    genuinely `null` on the renders between login and that load completing, while
 *    every consumer is typed to receive a `string`.
 *
 *    What that costs: `practitionerId` is interpolated into FHIR references
 *    (`Practitioner/${practitionerId}`) by the task, note and flag builders, so a
 *    write issued in that window records the literal reference
 *    `"Practitioner/null"` against a real clinical resource rather than failing.
 *
 *    Left declared `string` deliberately. Correcting it to `string | null` is the
 *    honest declaration and was measured: it surfaces 93 further diagnostics across
 *    22 file/code pairs, concentrated in the FHIR-reference builders
 *    (`taskHeader/addTask/hooks`, `assignTask/hooks/useTaskAssignment`,
 *    `taskDetails/cancel`, `flags/newFlag`, `notes/*`, `home/members/addCC`), each of
 *    which needs its own decision about whether to block the write, wait for the load,
 *    or record the reference anyway. That is a cycle of its own, not a half-treatment
 *    to bolt onto this one.
 */
export type AuthContextType = {
  eligibilityGroups?: EligibilityGroup[] | undefined;
  payers: Option[];
  practitionerId: string;
  practitionerName: string;
  token: string;
  userEmail: string;
  validateUSPSAddress: boolean;
  vendors: Option[];
  foodCardVendors: Option[];
  gender: string;
};

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);
export const AuthContextProvider = AuthContext.Provider;

/**
 * Read the auth context, narrowed to `AuthContextType`.
 *
 * A CAST, not a guard. Upstream consumers call `useContext(AuthContext)` directly and get
 * `AuthContextType | undefined`; an earlier form of this hook threw when the context was absent,
 * which emits and so was a runtime change rather than a narrowing.
 *
 * The rationale that throw carried — "reaching it was already fatal" — does not hold for the one
 * consumer that handles absence. `patient/notes/memberDocuments/index.tsx` reads `authInfo?.token`
 * at `:53` and `:81` (upstream `:49` and `:77`, its own optional chaining), so under upstream an
 * absent context yields a `null` SWR key — no fetch — and an early return from the upload and
 * delete handlers. The tab renders inert. It was not fatal there, and the throw would have made
 * it so.
 *
 * Every consumer does render beneath an `AuthContextProvider` today (`MainLayout` and `PatientTab`
 * both supply one, in both trees), so this is unreachable either way. The cast keeps it that way
 * without emitting.
 */
export function useAuthContext(): AuthContextType {
  return React.useContext(AuthContext) as AuthContextType;
}

export default AuthContext;
